// dashboard/src/components/credits/SubscriptionPlansCard.tsx
import React, { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { getSubscriptionPlans, subscribeToPlan, createRazorpayOrder, verifyRazorpayPayment } from '../../services/creditService';
import SubscriptionSuccessModal from '../billing/SubscriptionSuccessModal';

// Define Razorpay interface
declare global {
  interface Window {
    Razorpay: any;
  }
}

const SubscriptionPlansCard: React.FC = () => {
  const [error, setError] = useState<string | null>(null);
  const [processingPlanId, setProcessingPlanId] = useState<string | null>(null);
  const [selectedPlan, setSelectedPlan] = useState<any>(null);
  const [showSuccessModal, setShowSuccessModal] = useState<boolean>(false);
  const [successPlanData, setSuccessPlanData] = useState<any>(null);
  const queryClient = useQueryClient();

  const { data: plans, isLoading, error: plansError } = useQuery({
    queryKey: ['subscriptionPlans'],
    queryFn: getSubscriptionPlans,
  });

  // Load Razorpay script
  useEffect(() => {
    const script = document.createElement('script');
    script.src = 'https://checkout.razorpay.com/v1/checkout.js';
    script.async = true;
    document.body.appendChild(script);

    return () => {
      if (document.body.contains(script)) {
        document.body.removeChild(script);
      }
    };
  }, []);

  // Razorpay order creation
  const createOrderMutation = useMutation({
    mutationFn: createRazorpayOrder,
    onSuccess: (data) => {
      openRazorpayCheckout(data);
    },
    onError: (error: Error) => {
      setError(error.message);
      setProcessingPlanId(null);
    },
  });

  // Payment verification and subscription
  const verifyPaymentMutation = useMutation({
    mutationFn: verifyRazorpayPayment,
    onSuccess: () => {
      // After payment verification, subscribe to the plan
      subscribeMutation.mutate(selectedPlan.id);
    },
    onError: (error: Error) => {
      setError(error.message);
      setProcessingPlanId(null);
    },
  });

  // Plan subscription (after payment)
  const subscribeMutation = useMutation({
    mutationFn: (planId: string) => subscribeToPlan(planId, true), // payment_verified = true
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['creditBalance'] });
      queryClient.invalidateQueries({ queryKey: ['creditTransactions'] });

      // Show success modal with plan details
      setSuccessPlanData(selectedPlan);
      setShowSuccessModal(true);

      setError(null);
      setProcessingPlanId(null);
      setSelectedPlan(null);
    },
    onError: (error: Error) => {
      setError(error.message);
      setProcessingPlanId(null);
    },
  });

  const openRazorpayCheckout = (orderData: any) => {
    const options = {
      key: process.env.REACT_APP_RAZORPAY_KEY_ID,
      amount: orderData.amount,
      currency: orderData.currency,
      name: 'Crawly Carl',
      description: `${selectedPlan.name} Plan Subscription`,
      order_id: orderData.order_id,
      handler: async (response: any) => {
        // Verify payment and then subscribe
        verifyPaymentMutation.mutate({
          razorpay_payment_id: response.razorpay_payment_id,
          razorpay_order_id: response.razorpay_order_id,
          razorpay_signature: response.razorpay_signature,
          credits: 0, // Not applicable for subscriptions
          currency: orderData.currency,
        });
      },
      modal: {
        ondismiss: () => {
          setProcessingPlanId(null);
        },
      },
      theme: {
        color: '#2563eb',
      },
    };

    const razorpay = new window.Razorpay(options);
    razorpay.open();
  };

  const handleSubscribe = (plan: any) => {
    setError(null);
    setProcessingPlanId(plan.id);
    setSelectedPlan(plan);

    // Create Razorpay order for the plan price
    createOrderMutation.mutate({
      amount: Math.round(plan.price_usd * 100), // Convert to paise/cents
      currency: 'INR' // You can make this configurable
    });
  };

  if (isLoading) {
    return (
      <div className="bg-white shadow rounded-lg p-6 animate-pulse">
        <div className="h-8 bg-gray-200 rounded w-1/3 mb-4"></div>
        <div className="space-y-4">
          {[1, 2, 3].map((i) => (
            <div key={i} className="h-32 bg-gray-200 rounded"></div>
          ))}
        </div>
      </div>
    );
  }

  if (plansError) {
    return (
      <div className="bg-white shadow rounded-lg p-6 border-l-4 border-error-500">
        <h3 className="text-lg font-semibold text-gray-900 mb-2">Subscription Plans</h3>
        <p className="text-error-500">Error loading subscription plans. Please try again later.</p>
      </div>
    );
  }

  return (
    <div className="bg-white shadow rounded-lg p-6">
      <div className="text-center mb-6">
        <h3 className="text-2xl font-bold text-gray-900 mb-2">Choose Your Plan</h3>
        <p className="text-gray-600">Select a subscription plan that fits your usage needs</p>
      </div>

      {error && (
        <div className="mb-6 p-4 bg-red-100 border border-red-400 text-red-700 rounded-lg">
          <div className="flex items-center">
            <span className="text-red-500 mr-2">⚠️</span>
            {error}
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
        {plans?.map((plan) => (
          <div
            key={plan.id}
            className={`relative border-2 rounded-xl p-6 transition-all duration-200 flex flex-col h-full ${
              plan.id === 'pro'
                ? 'border-blue-500 bg-gradient-to-br from-blue-50 to-blue-100 shadow-lg transform scale-105'
                : 'border-gray-200 hover:border-gray-300 hover:shadow-md'
            }`}
          >
            {plan.id === 'pro' && (
              <div className="absolute -top-3 left-1/2 transform -translate-x-1/2 bg-gradient-to-r from-blue-500 to-blue-600 text-white text-sm px-4 py-1 rounded-full font-medium shadow-lg z-10">
                ⭐ Most Popular
              </div>
            )}

            <div className="text-center mb-6 pt-2">
              <h4 className="text-xl font-bold text-gray-900 mb-3">{plan.name}</h4>
              <div className="flex items-baseline justify-center mb-3">
                <span className="text-4xl font-bold text-gray-900">${plan.price_usd}</span>
                <span className="text-lg text-gray-500 ml-1">/month</span>
              </div>
              <p className="text-sm text-gray-600 leading-relaxed px-2">
                {plan.id === 'starter' && 'Perfect for small projects and testing'}
                {plan.id === 'pro' && 'For professionals and growing businesses'}
                {plan.id === 'business' && 'For data-driven companies and teams'}
              </p>
            </div>

            {/* Credit Breakdown */}
            <div className="bg-white rounded-lg p-4 mb-6 border border-gray-100 shadow-sm">
              <div className="text-center mb-4">
                <div className="text-3xl font-bold text-blue-600 mb-1">
                  {Math.round(plan.monthly_credits * (1 + plan.bonus_percentage / 100)).toLocaleString()}
                </div>
                <div className="text-sm text-gray-600 font-medium">Total Monthly Credits</div>
              </div>

              <div className="space-y-2 text-xs">
                <div className="flex justify-between items-center">
                  <span className="text-gray-600">Base Credits:</span>
                  <span className="font-medium text-gray-900">{plan.monthly_credits.toLocaleString()}</span>
                </div>
                {plan.bonus_percentage > 0 && (
                  <div className="flex justify-between items-center text-green-600">
                    <span>Bonus (+{plan.bonus_percentage}%):</span>
                    <span className="font-medium">
                      +{Math.round(plan.monthly_credits * plan.bonus_percentage / 100).toLocaleString()}
                    </span>
                  </div>
                )}
                <div className="border-t pt-2 flex justify-between items-center font-bold">
                  <span className="text-gray-900">Total:</span>
                  <span className="text-blue-600">
                    {Math.round(plan.monthly_credits * (1 + plan.bonus_percentage / 100)).toLocaleString()}
                  </span>
                </div>
              </div>
            </div>

            {/* Features */}
            <div className="space-y-3 mb-6 flex-grow">
              {plan.features.map((feature, index) => (
                <div key={index} className="flex items-start text-sm text-gray-700">
                  <span className="text-green-500 mr-3 text-base flex-shrink-0 mt-0.5">✓</span>
                  <span className="leading-relaxed">{feature}</span>
                </div>
              ))}
            </div>

            {/* Subscribe Button - Always at bottom */}
            <div className="mt-auto">
              <button
                onClick={() => handleSubscribe(plan)}
                disabled={processingPlanId === plan.id}
                className={`w-full py-3 px-6 rounded-lg text-sm font-semibold transition-all duration-200 ${
                  plan.id === 'pro'
                    ? 'bg-gradient-to-r from-blue-600 to-blue-700 text-white hover:from-blue-700 hover:to-blue-800 shadow-lg hover:shadow-xl disabled:from-blue-400 disabled:to-blue-500'
                    : 'bg-gray-800 text-white hover:bg-gray-900 shadow-md hover:shadow-lg disabled:bg-gray-400'
                } disabled:cursor-not-allowed transform hover:scale-105 active:scale-95`}
              >
                {processingPlanId === plan.id ? (
                  <div className="flex items-center justify-center">
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    Subscribing...
                  </div>
                ) : (
                  <div className="flex items-center justify-center">
                    <span className="mr-2">💳</span>
                    Subscribe to {plan.name}
                  </div>
                )}
              </button>

              {/* Plan Details */}
              <div className="mt-4 pt-4 border-t border-gray-200">
                <div className="text-xs text-gray-500 space-y-1.5">
                  <p className="flex items-center">
                    <span className="w-1 h-1 bg-gray-400 rounded-full mr-2 flex-shrink-0"></span>
                    Credits expire at end of monthly cycle
                  </p>
                  <p className="flex items-center">
                    <span className="w-1 h-1 bg-gray-400 rounded-full mr-2 flex-shrink-0"></span>
                    Plan credits used before custom credits
                  </p>
                  <p className="flex items-center">
                    <span className="w-1 h-1 bg-gray-400 rounded-full mr-2 flex-shrink-0"></span>
                    Bonus credits included in allocation
                  </p>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>

      <div className="mt-6 p-4 bg-gray-50 rounded-lg">
        <h4 className="text-sm font-medium text-gray-900 mb-2">How Plan Credits Work</h4>
        <ul className="text-xs text-gray-600 space-y-1">
          <li>• Plan credits are allocated monthly and expire at the end of each billing cycle</li>
          <li>• When you use credits, plan credits are consumed first, then custom credits</li>
          <li>• Unused plan credits do not roll over to the next month</li>
          <li>• You can purchase additional custom credits that never expire</li>
          <li>• Bonus credits are included in your monthly allocation</li>
        </ul>
      </div>

      {/* Success Modal */}
      {showSuccessModal && successPlanData && (
        <SubscriptionSuccessModal
          isOpen={showSuccessModal}
          onClose={() => setShowSuccessModal(false)}
          planName={successPlanData.name}
          planCredits={successPlanData.monthly_credits}
          bonusCredits={Math.round(successPlanData.monthly_credits * successPlanData.bonus_percentage / 100)}
        />
      )}
    </div>
  );
};

export default SubscriptionPlansCard;
